import { Dialog, DialogRef } from '@angular/cdk/dialog';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  signal,
  Signal,
  viewChild,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AppointmentStatusCodeV2, DeepgramFeature, PatientDetailsV2, PracticeOperatory, Provider, PmsType } from '@ffd/api-client';
import { pathParameter, PushTagOnClickDirective, TagEvent } from '@libs/shared-services';
import { provideVoiceChannel, VoiceInputType } from '@libs/voice-input';
import {
  AppointmentActionType,
  AppointmentReason,
  createAppointmentActions,
  END_HOUR,
  MAXIMUM_DEFAULT_APPOINTMENT_LENGTH,
  ScheduleCalendarTime,
  selectAppointmentActionType,
  selectEditedAppointment,
  selectEditedAppointmentPreRegistrationFakePatient,
  selectIsEditedAppointmentPreRegistration,
  selectSiteSettingsPmsType,
  selectSiteSettingsTimezone,
} from '@libs/store';
import {
  AddTreatmentItem,
  CheckboxComponent,
  ConfirmDialogComponent,
  ConfirmDialogResult,
  DropdownComponent,
  DropdownItem,
  DropdownItemPrefixDirective,
  SelectProviderComponent,
  TooltipModule,
  VoiceTypingTextFieldComponent,
} from '@libs/ui-lib';
import { getPmsDisplayCode, routes } from '@libs/utils';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  DeleteAppointmentModalComponent,
  DeleteAppointmentModalComponentData,
  DeleteAppointmentModalComponentResult,
} from '@libs/schedule';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DateTime } from 'luxon';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { Subscription } from 'rxjs';
import { AddNewTreatmentData } from '@libs/charting';
import { PatientSelectorComponent } from '../patient-selector';
import { SchedulesService } from '../services/schedules.service';
import { CompleteAppointmentService } from '../services/complete-appointment.service';
import { CreateAppointmentService } from './create-appointment.service';
import {
  TimePatternEditorModalComponent,
  TimePatternEditorModalComponentData,
  TimePatternEditorModalComponentResult,
} from './time-pattern-editor-modal';
import { ReasonSelectorModalComponent, ReasonSelectorModalResult } from './reason-selector-modal/reason-selector-modal.component';
import { SelectReasonComponent } from './select-reason';
import { DateTimeSelectorComponent } from './date-time-selector/date-time-selector.component';
import { ReasonSelectorModalData } from './reason-selector-modal/reason-selector-modal.service';

@Component({
  selector: 'lib-create-appointment',
  imports: [
    CommonModule,
    TranslateModule,
    SvgIconComponent,
    PatientSelectorComponent,
    DropdownComponent,
    DropdownItemPrefixDirective,
    SelectReasonComponent,
    SelectProviderComponent,
    DateTimeSelectorComponent,
    CheckboxComponent,
    ReactiveFormsModule,
    PushTagOnClickDirective,
    LottieComponent,
    VoiceTypingTextFieldComponent,
    TooltipModule,
  ],
  templateUrl: './create-appointment.component.html',
  styleUrl: './create-appointment.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CreateAppointmentService, provideVoiceChannel(VoiceInputType.VoiceTyping, DeepgramFeature.NOTE_VOICE)],
})
export class CreateAppointmentComponent implements OnDestroy, OnInit {
  @HostListener('document:keydown.escape', ['$event']) public onKeydownHandler(_: Event): void {
    if (!this.isDirty()) {
      this.closeCreateAppointment();
    }
  }

  @ViewChild('voiceTypingTextField') private readonly voiceTypingTextField?: VoiceTypingTextFieldComponent;
  @ViewChild('scrollArea', { static: true }) public scrollArea: ElementRef<HTMLElement> | undefined;
  @ViewChild('reasonSelector') private readonly selectReasonComponent?: SelectReasonComponent;

  private readonly appointmentStatusDropdown = viewChild<DropdownComponent<AppointmentStatusCodeV2>>('appointmentStatusDropdown');

  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly createAppointmentService = inject(CreateAppointmentService);
  private readonly schedulesService = inject(SchedulesService);
  private readonly completeAppointmentService = inject(CompleteAppointmentService);
  private readonly store = inject(Store);

  private readonly injector = inject(Injector);
  private readonly dialog = inject(Dialog);
  private readonly destroyRef = inject(DestroyRef);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private dialogRef: DialogRef<any> | null = null;
  private transcriptionSubscription: Subscription | null = null;
  private readonly siteTimezone = this.store.selectSignal(selectSiteSettingsTimezone);
  private readonly correctedAppointmentDate = computed(() => {
    const siteTimezone = this.siteTimezone();

    if (!siteTimezone) {
      return this.appointmentDate().toFormat('yyyy-MM-dd');
    }

    return this.appointmentDate().setZone(siteTimezone).toFormat('yyyy-MM-dd');
  });

  private readonly isRecording = signal(false);
  private readonly pendingAppointmentStatus = signal<AppointmentStatusCodeV2 | null>(null);
  protected readonly appointmentActionType = this.store.selectSignal(selectAppointmentActionType);
  protected readonly isEditAppointment = computed(() => this.appointmentActionType() === AppointmentActionType.Edit);
  protected readonly isEditedAppointmentPreRegistration = this.store.selectSignal(selectIsEditedAppointmentPreRegistration);
  protected readonly editedAppointmentPreRegistrationFakePatient = this.store.selectSignal(
    selectEditedAppointmentPreRegistrationFakePatient,
  );
  private readonly pmsType = this.store.selectSignal(selectSiteSettingsPmsType);
  protected readonly isOpenDental = computed(() => this.pmsType() === PmsType.OPEN_DENTAL);
  protected readonly isNotesFocused = signal(false);
  protected readonly notesCharCount = signal(0);
  protected readonly NOTES_MAX_LENGTH = 4000;
  protected readonly isNotesOverMaxLength = computed(() => this.notesCharCount() > this.NOTES_MAX_LENGTH);
  protected readonly reasonSubmitAttempted = signal(false);

  protected sidebarTitle: Signal<string> = computed(() => {
    if (this.isEditAppointment()) {
      return 'home_right_side_bar_edit_appointment';
    }
    return 'home_right_side_bar_new_appointment';
  });

  protected readonly isDirty = computed(() => this.createAppointmentService.viewModel().isDirty);
  protected readonly timePatterns = computed(() => this.createAppointmentService.viewModel().timePatterns);
  protected readonly appointmentDate = computed(() => this.createAppointmentService.viewModel().appointmentDate);
  protected readonly startTime = computed(() => this.createAppointmentService.viewModel().fromTime);
  protected readonly endTime = computed(() => this.createAppointmentService.viewModel().toTime);
  protected readonly timeBlock = computed(() => this.createAppointmentService.viewModel().timeBlock);
  protected readonly appointmentLength = computed(() => this.createAppointmentService.viewModel().appointmentLength);
  protected readonly activatedRouteParams = toSignal(this.activatedRoute.paramMap);
  protected readonly patient = computed(() => this.createAppointmentService.viewModel().patient);
  protected readonly siteId = computed(() => this.activatedRouteParams()?.get(pathParameter('siteId')));
  protected readonly isShowcase = computed(() => this.siteId() === routes.showcaseSiteId);
  protected readonly saveInProgress = computed(() => this.schedulesService.viewModel().saveInProgress);
  protected readonly errorMessage = computed(() => this.schedulesService.viewModel().sidebarErrorMessage);
  public provider = computed(() => this.createAppointmentService.viewModel().selectedProvider);
  private readonly editedAppointment = this.store.selectSignal(selectEditedAppointment);
  public secondaryProvider = computed(() => this.editedAppointment()?.appointment.additionalProvider);
  public secondaryProviderDisplay = computed(() => {
    const sp = this.secondaryProvider();
    return sp ? `${sp.firstName} ${sp.lastName} (${getPmsDisplayCode(sp)})` : '';
  });
  public allOperatoryOptions = computed(() => this.createAppointmentService.viewModel().allOperatoryOptions);
  public operatory = computed(() => this.createAppointmentService.viewModel().selectedOperatory);
  public appointmentStatusOptions = computed(() => this.createAppointmentService.viewModel().appointmentStatusOptions);
  public selectedAppointmentStatus = computed(() => this.createAppointmentService.viewModel().selectedAppointmentStatus);
  public completedStatusOption = computed(() => this.createAppointmentService.viewModel().completedAppointmentStatus);
  public reasonOptions = computed(() => this.createAppointmentService.viewModel().reasonOptions);
  public selectedReasons = computed(() => {
    return this.createAppointmentService.viewModel().selectedReasons;
  });
  protected readonly selectedTreatments = computed<AddNewTreatmentData[]>(() => {
    return this.completeAppointmentService.mapProceduresToTreatments(this.selectedReasons());
  });
  public isReasonsDataLoading = computed(() => this.createAppointmentService.viewModel().isReasonsDataLoading);
  protected notes = computed(() => this.createAppointmentService.viewModel().notes);
  protected teeth = computed(() => this.createAppointmentService.viewModel().teeth);

  protected transcriptionControl = this.createAppointmentService.transcriptionControl;
  protected readonly dictationFinalized$ = this.createAppointmentService.dictationFinalized$;
  protected transcriptionStatus = this.createAppointmentService.transcriptionStatus;
  protected readonly transcriptionStatus$ = toObservable(this.transcriptionStatus);
  protected lastFocusedCursorOffset = this.createAppointmentService.lastFocusedCursorOffset;
  protected isNotesInputEmpty = signal(this.notes()?.trim().length === 0);

  public timeOverlayHeight = '160px';
  public timeOverlayWidth = '270px';
  public overlayPositionStrategy: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 10,
    },
  ];

  public tentativeForm = new FormGroup({
    isTentative: new FormControl(this.createAppointmentService.viewModel().isTentative ?? false),
  });

  protected TagEvent = TagEvent;

  public readonly lottieOptions: AnimationOptions = {
    path: '/assets/animations/loading-spinner.json',
  };

  public get appointmentLengths(): DropdownItem<number>[] {
    const minutesFromStartTimeToEndHour = END_HOUR * 60 - (this.startTime().hour * 60 + this.startTime().minute);
    const maxIntervals = Math.min(MAXIMUM_DEFAULT_APPOINTMENT_LENGTH, minutesFromStartTimeToEndHour);
    return Array.from({ length: maxIntervals / this.timeBlock() }, (_, index) => index + 1).map((value) => {
      const appointmentLength = value * this.timeBlock();
      return {
        label: `${appointmentLength} mins`,
        value: appointmentLength,
      };
    });
  }

  protected operatoryCompareFn = (o1: PracticeOperatory, o2: PracticeOperatory): boolean => o1.id === o2.id;

  public ngOnInit(): void {
    this.createAppointmentService.init();
    if (this.isEditAppointment() && this.patient()) {
      this.createAppointmentService.loadTeeth(this.patient()!, this.siteId()!);
    }
    this.notesCharCount.set(this.notes()?.length ?? 0);
    this.transcriptionControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.updateNotesCharCount();
    });
  }

  public ngOnDestroy(): void {
    if (this.isRecording()) {
      this.toggleDictationClick();
    }
    this.createAppointmentService.clearPatientSearchTerm();
  }

  public closeCreateAppointment(): void {
    this.schedulesService.removeAppointmentSelection();
    this.schedulesService.editSelectedAppointment({ isEdited: false });
    this.store.dispatch(createAppointmentActions.onResetCreateAppointment());
    this.pendingAppointmentStatus.set(null);
  }

  public onCloseClick(): void {
    if (this.isRecording()) {
      this.toggleDictationClick();
    }

    const hasNotesChanged = this.notes() !== this.getTextFieldNotes();
    const hasPendingStatusChange =
      this.pendingAppointmentStatus() != null && this.pendingAppointmentStatus()?.id !== this.selectedAppointmentStatus()?.id;

    if (this.isDirty() || hasNotesChanged || hasPendingStatusChange) {
      this.dialogRef = this.dialog.open<ConfirmDialogResult>(ConfirmDialogComponent, {
        backdropClass: ['bg-overlay-light'],
        panelClass: ['w-[480px]'],
        disableClose: true,
        data: {
          title: 'schedule_exit_modal_title',
          description: 'schedule_exit_modal_description',
          cancelLabel: 'schedule_exit_modal_continue',
          confirmLabel: 'schedule_exit_modal_discard',
        },
      });

      this.dialogRef.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
        if (result.confirm) {
          this.closeCreateAppointment();
        }
      });
    } else {
      this.closeCreateAppointment();
    }
  }

  public onOperatoryOptionChange(option?: PracticeOperatory | (PracticeOperatory | undefined)[] | null): void {
    if (option && !Array.isArray(option)) {
      this.createAppointmentService.setOperatory(option);
    }
  }

  public onAppointmentStatusOptionChange(option?: AppointmentStatusCodeV2 | (AppointmentStatusCodeV2 | undefined)[] | null): void {
    if (!option || Array.isArray(option)) {
      return;
    }

    if (this.isEditAppointment()) {
      this.pendingAppointmentStatus.set(option);
      return;
    }

    const isCompletedStatus = option.id === this.completedStatusOption()?.id;
    const hasTreatments = this.selectedTreatments().length > 0;

    if (isCompletedStatus && hasTreatments) {
      this.handleCompleteAppointmentFlow(option);
    } else {
      this.createAppointmentService.setAppointmentStatus(option);
    }
  }

  private handleCompleteAppointmentFlow(statusOption: AppointmentStatusCodeV2): void {
    const patient = this.patient();
    if (!patient) {
      return;
    }

    this.completeAppointmentService
      .openCompleteAppointmentModal$({
        patient,
        appointmentDate: this.appointmentDate(),
        procedures: this.selectedReasons(),
        teeth: this.teeth(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((completedTreatments) => {
        this.handleCompleteAppointmentResult(completedTreatments, statusOption, patient);
      });
  }

  private handleCompleteAppointmentResult(
    completedTreatments: AddTreatmentItem[] | undefined,
    statusOption: AppointmentStatusCodeV2,
    patient: PatientDetailsV2,
    onApplied?: () => void,
  ): void {
    // undefined means user cancelled or clicked skip - revert status in create mode only
    if (completedTreatments === undefined) {
      if (!onApplied) {
        const appointmentStatusDropdown = this.appointmentStatusDropdown();
        const previousAppointmentStatus = this.selectedAppointmentStatus();
        if (appointmentStatusDropdown && previousAppointmentStatus) {
          appointmentStatusDropdown.writeValue(previousAppointmentStatus);
        }
        this.pendingAppointmentStatus.set(null);
      }
      return;
    }

    this.createAppointmentService.setAppointmentStatus(statusOption);

    // Save treatments if any were selected
    if (completedTreatments.length > 0) {
      this.completeAppointmentService.completeTreatments({
        treatments: completedTreatments,
        patientId: patient.id,
        siteId: this.siteId()!,
        appointmentDate: this.correctedAppointmentDate(),
      });
    }

    this.pendingAppointmentStatus.set(null);
    if (onApplied) {
      onApplied();
    }
  }

  public onSaveTentativeClick(): void {
    if (this.isOpenDental() && !this.validateReasonSelection()) {
      this.reasonSubmitAttempted.set(true);
      return;
    }
    if (this.isNotesOverMaxLength()) {
      return;
    }
    const wasRecording = this.isRecording();
    if (wasRecording) {
      this.toggleDictationClick();
    }
    // this setTimeout is necessary to ensure that the appointment is saved after the dictation is stopped, and we have the note ready
    setTimeout(() => {
      this.schedulesService.saveAppointment({
        type: 'create',
        isTentative: true,
        notes: this.getTextFieldNotes(wasRecording),
      });
      this.store.dispatch(createAppointmentActions.onResetCreateAppointment());
    });
  }

  public onCreateClick(): void {
    if (this.isOpenDental() && !this.validateReasonSelection()) {
      this.reasonSubmitAttempted.set(true);
      return;
    }
    if (this.isNotesOverMaxLength()) {
      return;
    }
    const wasRecording = this.isRecording();
    if (wasRecording) {
      this.toggleDictationClick();
    }
    // this setTimeout is necessary to ensure that the appointment is saved after the dictation is stopped,  and we have the note ready
    setTimeout(() => {
      this.schedulesService.saveAppointment({
        type: 'create',
        isTentative: false,
        notes: this.getTextFieldNotes(wasRecording),
      });
    });
  }

  public onDeleteClick(): void {
    if (this.isRecording()) {
      this.toggleDictationClick();
    }
    this.dialog
      .open<DeleteAppointmentModalComponentResult, DeleteAppointmentModalComponentData>(DeleteAppointmentModalComponent, {
        backdropClass: ['bg-overlay-light'],
        panelClass: ['w-[480px]'],
        disableClose: true,
      })
      .closed.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.isDeleted) {
          this.schedulesService.deleteAppointment();
        }
      });
  }

  public onSaveChangesClick(): void {
    if (this.isOpenDental() && !this.validateReasonSelection()) {
      this.reasonSubmitAttempted.set(true);
      return;
    }
    if (this.isNotesOverMaxLength()) {
      return;
    }
    const wasRecording = this.isRecording();
    if (wasRecording) {
      this.toggleDictationClick();
    }

    const statusToApply = this.pendingAppointmentStatus() ?? this.selectedAppointmentStatus();
    const isCompletedStatus = statusToApply?.id === this.completedStatusOption()?.id;
    const hasTreatments = this.selectedTreatments().length > 0;

    if (statusToApply && isCompletedStatus && hasTreatments) {
      const patient = this.patient();
      if (!patient) {
        return;
      }

      this.completeAppointmentService
        .openCompleteAppointmentModal$({
          patient,
          appointmentDate: this.appointmentDate(),
          procedures: this.selectedReasons(),
          teeth: this.teeth(),
        })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((completedTreatments) => {
          this.handleCompleteAppointmentResult(completedTreatments, statusToApply, patient, () => this.performEditSave(wasRecording));
        });
      return;
    }

    const pendingStatus = this.pendingAppointmentStatus();
    if (pendingStatus) {
      this.createAppointmentService.setAppointmentStatus(pendingStatus);
      this.pendingAppointmentStatus.set(null);
    }

    this.performEditSave(wasRecording);
  }

  private performEditSave(wasRecording: boolean): void {
    // this setTimeout is necessary to ensure that the appointment is saved after the dictation is stopped, and we have the note ready
    setTimeout(() => {
      this.schedulesService.saveAppointment({
        type: 'edit',
        isTentative: !!this.tentativeForm.controls.isTentative.value,
        notes: this.getTextFieldNotes(wasRecording),
      });
    });
  }

  // eslint-disable-next-line rxjs/finnish
  public openTimePatternEditorModal(data: TimePatternEditorModalComponentData): void {
    this.dialogRef = this.dialog.open<TimePatternEditorModalComponentResult, TimePatternEditorModalComponentData>(
      TimePatternEditorModalComponent,
      {
        backdropClass: ['bg-overlay-light'],
        disableClose: true,
        data,
      },
    );

    this.dialogRef.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (!result) {
        return;
      }

      this.createAppointmentService.setTimePatterns(result.timePatterns);
    });
  }

  public onDateSelect(appointmentDate: DateTime): void {
    this.createAppointmentService.setAppointmentDate(appointmentDate);
    this.schedulesService.setDate(appointmentDate.plus(0));
  }

  public onStartTimeSet(startTime: ScheduleCalendarTime): void {
    this.createAppointmentService.setStartTime(startTime);
  }

  public onEndTimeSet(endTime: ScheduleCalendarTime): void {
    this.createAppointmentService.setEndTime(endTime);
  }

  protected appointmentLengthChange(value: number | number[] | null): void {
    if (value && !Array.isArray(value)) {
      this.createAppointmentService.setAppointmentLength(value);
    }
  }

  protected onPatientSelect({
    patient,
    isInitialPatientSelect = false,
  }: {
    patient: PatientDetailsV2;
    isInitialPatientSelect?: boolean;
  }): void {
    this.createAppointmentService.setPatient(patient, isInitialPatientSelect);
    this.createAppointmentService.loadTeeth(patient, this.siteId()!);
  }

  public openReasonEditorModal(data: ReasonSelectorModalData): void {
    this.dialogRef = this.dialog.open<ReasonSelectorModalResult, ReasonSelectorModalData>(ReasonSelectorModalComponent, {
      backdropClass: ['bg-overlay-light'],
      disableClose: true,
      injector: this.injector,
      data,
    });

    this.dialogRef.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (!result) {
        return;
      }

      this.createAppointmentService.setReasons(result.addedReasons);
    });
  }

  protected onSelectReason(reason: AppointmentReason): void {
    this.createAppointmentService.setReasons([...this.selectedReasons(), reason]);
  }

  protected onDeselectReason(reason: AppointmentReason): void {
    const remainingReasons =
      reason.progressNoteId == null
        ? this.selectedReasons().filter((r) => !(r.progressNoteId == null && r.code === reason.code))
        : this.selectedReasons().filter((r) => r.progressNoteId !== reason.progressNoteId);
    this.createAppointmentService.setReasons(remainingReasons);
  }

  public onProviderOptionChange(provider: Provider): void {
    this.createAppointmentService.setProvider(provider);
  }

  protected toggleDictationClick(): void {
    this.isRecording.set(!this.isRecording());
    this.createAppointmentService.toggleSpeechRecognitionListening(this.voiceTypingTextField?.getTemplateText() ?? '');

    if (this.transcriptionSubscription) {
      this.transcriptionSubscription.unsubscribe();
      this.transcriptionSubscription = null;
    }

    this.transcriptionSubscription = this.transcriptionControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.scrollArea?.nativeElement.scrollTo({
        top: this.scrollArea?.nativeElement.scrollHeight,
        behavior: 'smooth',
      });
    });
  }
  protected updateLastFocusedCursorOffset(offset: number): void {
    this.createAppointmentService.updateLastFocusedCursorOffset(offset);
  }

  protected onRecordingLoading(notes: string): void {
    this.createAppointmentService.commitDictatedText(notes);
  }

  protected onRecordingStopped(notes: string): void {
    this.createAppointmentService.commitDictatedText(notes);
  }

  /**
   * `wasRecording` must be captured BEFORE the caller's toggleDictationClick() stopped dictation, not read fresh -
   * `isRecording()` has already flipped to false by the time this runs. When true, mainContent's DOM is not
   * trustworthy here: whether its rebuild from the corrected notes() has completed by the time this setTimeout
   * callback fires depends on Angular's effect/CD scheduling relative to a macrotask. notes() has no such problem -
   * it recomputes synchronously on read - and commitDictatedText (via recordingStopped, off the same
   * toggleDictationClick() call through a microtask-scheduled effect chain guaranteed to resolve before any
   * setTimeout) has already written the merged text into it. When false, nothing just stopped, so the DOM is read
   * instead - it may hold manual edits never written to state.notes.
   */
  protected getTextFieldNotes(wasRecording = false): string {
    if (wasRecording) {
      return this.notes();
    }

    return this.voiceTypingTextField?.getTemplateText() ?? '';
  }

  protected onNotesClick(): void {
    this.isNotesFocused.set(true);
  }

  protected onNotesFocusOut(ev: FocusEvent, self: HTMLElement): void {
    this.isNotesInputEmpty.set(this.voiceTypingTextField?.getTemplateText().trim().length === 0);
    if (self.contains(ev.relatedTarget as Node)) {
      return;
    }
    this.isNotesFocused.set(false);
  }

  protected onNotesChange(text: string): void {
    this.notesCharCount.set(text.length);
  }

  private updateNotesCharCount(): void {
    this.notesCharCount.set(this.getTextFieldNotes().length);
  }

  private validateReasonSelection(): boolean {
    return !(!this.selectedReasons() || this.selectedReasons().length === 0);
  }
}
